import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { verifyAuthCode } from "~/services/loginService";
import { getMe } from "~/services/meServices";
import { getOneBookingTransaction } from "~/services/bookingTransactionService";
import checkoutServices from "~/services/checkoutServices";
import authAction from "~/store/actions/authAction";
import meAction from "~/store/actions/meAction";
import businessAction from "~/store/actions/businessAction";
import authService from "~/services/authService";
import SpinnerLoading from "~/components/common/spinnerLoading/SpinnerLoading";

const CheckoutDeepLink = () => {
	const dispatch = useDispatch();
	const { id: businessId, bookingTransactionId } = useParams();
	const location = useLocation();
	const history = useHistory();
	const auth = getAuth();
	const isAuthInProgressRef = useRef(false);

	const searchParams = new URLSearchParams(location.search);
	const authCode = searchParams.get("auth_code");
	const type = searchParams.get("type");
	const pixBusiness = searchParams.get("pix_business");

	useEffect(() => {
		if (!authCode || isAuthInProgressRef.current) return;
		isAuthInProgressRef.current = true;

		const run = async () => {
			try {
				await authService.waitForAuthIdle();

				const result = await verifyAuthCode(authCode);
				if (result?.data?.status !== "OK" || !result?.data?.message) {
					history.push(`/business/link/${businessId}`);
					return;
				}

				const userCredential = await signInWithCustomToken(auth, result.data.message);
				const firebaseUser = userCredential?.user;
				if (!firebaseUser) {
					history.push(`/business/link/${businessId}`);
					return;
				}

				const idToken = await firebaseUser.getIdToken();
				dispatch(authAction.setUser(firebaseUser));
				dispatch(meAction.setMe(idToken));
				localStorage.setItem("@prit-web/accessToken", idToken);
				localStorage.setItem("@prit-web/accessTokenExpirationTime", String(Date.now() + 3600000));

				const meResponse = await getMe();
				const userData = meResponse?.data;
				if (userData) {
					localStorage.setItem("@prit-web/meData", JSON.stringify(userData));
					if (userData.id) {
						localStorage.setItem("@prit-web/userId", userData.id);
					}
				}

				dispatch(businessAction.getBusiness(businessId));

				const btResponse = await getOneBookingTransaction(bookingTransactionId);
				const bookingData = btResponse?.data;

				if (!bookingData) {
					history.push(`/business/link/${businessId}`);
					return;
				}

				const formattedServices = (bookingData?.bookings || []).map((booking) => ({
					date: booking?.startTime,
					dateStart: booking?.startTime,
					professional: {
						avatarUrl: booking?.professional?.avatarUrl || null,
						duration: booking?.duration,
						id: booking?.professional?.id,
						name: booking?.professional?.name || "Profissional",
						price: booking?.price,
						pricingType: booking?.pricingType?.name || booking?.pricingType,
					},
					professional_id: booking?.professional?.id,
					service: {
						serviceDuration: booking?.duration,
						serviceName: booking?.service?.name,
						servicePrice: booking?.price,
					},
					service_id: booking?.service?.id,
				}));

				// if (type === "pix_balcao" && pixBusiness) {
				// 	history.push(`/later-payment/${businessId}/bookingTransactionId/${bookingTransactionId}`, {
				// 		services: formattedServices,
				// 		laterPayment: true,
				// 		paymentType: type,
				// 		pixBusiness,
				// 		recipientId: bookingData?.recipientId,
				// 	});
				// 	return;
				// }

				if (userData?.id) {
					const payResponse = await checkoutServices.fetchPaymentRequest(userData.id);
					const payments = payResponse?.data;

					const paymentRequest = payments?.find((p) =>
						p.booking_transaction_references?.some(
							(ref) => String(ref.booking_transaction_id) === String(bookingTransactionId)
						)
					);

					if (paymentRequest) {
						const anticipatedResponse = await checkoutServices.fetchPaymentAnticipatedAmountByBookTransaction({
							recipient_id: paymentRequest.recipient_id || bookingData?.recipientId,
							payment_request_id: paymentRequest.payment_request_id,
						});

						const postPayment = {
							booking_external_id: bookingTransactionId,
							recipient_id: paymentRequest.recipient_id || bookingData?.recipientId,
							payment_request_id: paymentRequest.payment_request_id,
							checkoutInformation: anticipatedResponse?.data,
							amount: paymentRequest.amount - (paymentRequest?.discount || 0),
						};

						history.push(`/later-payment/${businessId}/bookingTransactionId/${bookingTransactionId}`, {
							postPayment,
							services: formattedServices,
							laterPayment: true,
							paymentType: type,
						});
						return;
					}
				}

				history.push(`/checkout/${businessId}`, {
					services: formattedServices,
					paymentType: type,
					recipientId: bookingData?.recipientId,
				});
			} catch (err) {
				console.error("CheckoutDeepLink error:", err);
				history.push(`/business/link/${businessId}`);
			} finally {
				isAuthInProgressRef.current = false;
			}
		};

		run();
	}, [authCode]);

	return <SpinnerLoading loading />;
};

export default CheckoutDeepLink;
